import React, { use, useCallback, useEffect, useState } from "react";
import {
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements,
  CardElement,
} from "@stripe/react-stripe-js";
import { useRouter } from "next/router";
import { AiOutlineUser, AiOutlineMail, AiOutlineSearch } from "react-icons/ai";
import { RiLockPasswordLine } from "react-icons/ri";
import Input from "../form/Input";
import Checkbox from "../form/Checkbox";
import AppService from "../../services/AppService";
import { useCookies } from "react-cookie";
import { getUTMParams } from "../../hooks/useUTMParams";
import { authPostRequest } from "../../utils/authGetRequest";
import Utils, { calculateDiscount, planPrice } from "../../utils/Utils";

const planName = {
  weekly: "Standard Weekly",
  monthly: "Standard Monthly",
  yearly: "Standard Yearly",
  "weekly-premium": "Weekly Premium",
  "monthly-premium": "Monthly Premium",
  "yearly-premium": "Annual Premium",
  "nba-playoff": "NBA PLAYOFF",
  "wnba-season": "WNBA SEASON",
  "day-pass": "24 Hour Premium",
  "content-monthly": "Content Monthly",
  "premium-weekly-25": "Premium Weekly 2025-26",
  "premium-monthly-25": "Premium Monthly 2025-26",
  "premium-annual-25": "Premium Annual 2025-26",
  "standard-weekly-25": "Standard Weekly 2025-26",
  "standard-monthly-25": "Standard Monthly 2025-26",
  "standard-annual-25": "Standard Annual 2025-26",
};
const clearSQCodeFromLocalStorage = () => {
  if (typeof window !== "undefined") {
    localStorage.removeItem("sq_code");
  }
};

const planDuration = {
  weekly: "weekly",
  monthly: "monthly",
  yearly: "yearly",
  "weekly-premium": "weekly",
  "monthly-premium": "monthly",
  "yearly-premium": "yearly",
  "nba-playoff": "yearly",
  "wnba-season": "yearly",
  "day-pass": "day",
  "content-monthly": "monthly",
  "premium-weekly-25": "weekly",
  "premium-monthly-25": "monthly",
  "premium-annual-25": "yearly",
  "standard-weekly-25": "weekly",
  "standard-monthly-25": "monthly",
  "standard-annual-25": "yearly",
};

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      color: "#000",
      fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
      fontSmoothing: "antialiased",
      fontSize: "16px",
      "::placeholder": {
        color: "#aab7c4",
      },
    },
    invalid: {
      color: "#fa755a",
      iconColor: "#fa755a",
    },
  },
};

const CheckoutForm = ({ plan, user, page }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [termsChecked, setTermsChecked] = useState(false);
  const [receivePicksChecked, setReceivePicksChecked] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const stripe = useStripe();
  const elements = useElements();
  const router = useRouter();
  const [cookies, setCookie] = useCookies(["token_access, name"]);
  const [showCouponInput, setShowCouponInput] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [couponDetails, setCouponDetails] = useState(null);
  const [couponSuccess, setCouponSuccess] = useState("");
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [timeLeft, setTimeLeft] = useState({});

  const validateEmail = (email) => {
    const re = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
    return re.test(String(email).toLowerCase());
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsLoading(true);
    setErrorMessage(() => "");

    try {
      if (!validateEmail(email)) {
        setErrorMessage("Invalid email format.");
        throw new Error("Invalid email format.");
      }
      if (!user && !name && page !== "marchMadnessPrimer") {
        setErrorMessage("Name is required.");
        throw new Error("Name is required.");
      }
      if (!user && password.length < 6 && page !== "marchMadnessPrimer") {
        setErrorMessage("Password should be at least 6 characters long.");
        throw new Error("Short password.");
      }
      if (!user && password !== confirmPassword && page !== "marchMadnessPrimer") {
        setErrorMessage("Passwords do not match.");
        throw new Error("Mismatched passwords.");
      }
      if (!termsChecked) {
        setErrorMessage("You must accept the terms and conditions.");
        throw new Error("Terms not accepted.");
      }
      if (!stripe || !elements) {
        throw new Error("Stripe or elements not initialized.");
      }

      const card = elements.getElement(CardNumberElement);

      if (page === "marchMadnessPrimer") {
        const response = await fetch(
          `${process.env.NEXT_PUBLIC_API_URL_API_URL}/api/march-madness-primer/create-payment-intent`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ amount: 1999, couponDetails }), // Example amount in cents
          }
        );
        const paymentIntent = await response.json();

        const result = await stripe.confirmCardPayment(paymentIntent.clientSecret, {
          payment_method: {
            card: elements.getElement(CardNumberElement),
            billing_details: {
              email: email,
            },
          },
        });

        if (result.error) {
          console.log(result.error.message);
          setErrorMessage(result.error.message);
        } else {
          if (result.paymentIntent.status === "succeeded") {
            fetch(`${process.env.NEXT_PUBLIC_API_URL_API_URL}/api/klaviyo/march_madness_primer/${email}/save`, {
              method: "GET",
            })
              .then((response) => response.json())
              .then((data) => {
                setCookie("march_madness_primer", true, {
                  maxAge: 31536000,
                  path: "/",
                });
              })
              .then(() => {
                clearSQCodeFromLocalStorage();
                const a = document.createElement("a");
                a.href = "/march-madness-primer-welcome";
                a.click();
              });
          }
        }
        setIsLoading(false);
        return;
      }

      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: "card",
        card: card,
      });

      if (error) {
        setErrorMessage(error.message);
        throw error;
      }

      const subscriptionData = {
        subscriptionType: plan,
        name: name,
        email: email,
        password: password,
        paymentMethodId: paymentMethod.id,
      };

      // Add coupon ID to the request if a valid coupon has been applied
      if (couponDetails && couponDetails.id) {
        subscriptionData.couponId = couponDetails;
      }

      const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL_API_URL}/api/create-subscription`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(subscriptionData),
      });

      const responseData = await response.json();

      if (responseData.error) {
        let error = responseData.error.split(":", 2)[1]
          ? responseData.error.split(":", 2)[1]
          : "We're sorry, something went wrong. Please try again later.";
        setErrorMessage(error);
        throw new Error(responseData.error);
      }

      if (responseData.requiresAction) {
        const { error: confirmationError } = await stripe.handleCardAction(responseData.paymentIntentClientSecret);
        if (confirmationError) {
          setErrorMessage(confirmationError.message);
          throw confirmationError;
        }
      }
      // Set user in context
      const appService = new AppService(responseData.access_token);
      let acc = await appService.get("api/account");
      localStorage.setItem("user", JSON.stringify(acc));
      appService.setToken(responseData.access_token);
      appService.setUser(responseData.name);

      let d = new Date();
      d.setTime(d.getTime() + 1440 * 60 * 1000 * 30);
      setCookie("token_access", responseData.access_token, {
        path: "/",
        expires: d,
      });
      setCookie("name", responseData.name, { path: "/", expires: d });

      try {
        const utmParams = getUTMParams() || {};
        await authPostRequest("/api/utm", responseData.access_token, utmParams);
      } catch (err) {
        console.error(err);
      }
      clearSQCodeFromLocalStorage();
      const a = document.createElement("a");
      a.href = "/nba/shotquality-scores";
      a.click();
    } catch (err) {
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCouponValidation = useCallback(
    async (coupon = "") => {
      setCouponSuccess(""); // Clear the previous success message
      setErrorMessage(() => "");
      try {
        const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL_API_URL}/api/subscription/validate-coupon`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            coupon: couponCode || coupon,
            plan: page === "marchMadnessPrimer" ? "march-madness-primer" : plan,
          }),
        });

        const data = await response.json();
        if (data.data) {
          setCouponDetails(data.data);
          setCouponDiscount(
            calculateDiscount(data.data, page === "marchMadnessPrimer" ? "march-madness-primer" : plan)
          );
          setCouponSuccess("Coupon applied successfully!"); // Set the success message
          setErrorMessage(""); // Clear any previous error messages
        } else {
          setErrorMessage("Invalid coupon code or coupon not available.");
        }
      } catch (error) {
        setErrorMessage("Failed to validate coupon.");
      }
    },
    [couponCode, plan]
  );

  useEffect(() => {
    if (router.isReady) {
      const queryCoupon = router.query.coupon || router.query.sq_code || localStorage.getItem("sq_code");

      if (typeof queryCoupon === "string" && queryCoupon !== "") {
        setCouponCode(queryCoupon);
        setShowCouponInput(true);
        setTimeout(() => {
          handleCouponValidation();
        }, 0);
      }
      if (user) {
        setName(user?.name);
        setEmail(user?.email);
      }
    }
  }, [router, handleCouponValidation, user]);

  // useEffect(() => {
  //   if (user && page === "marchMadnessPrimer") {
  //     const isStandardUser = Utils.hasStandardPlan(user);
  //     if (isStandardUser) {
  //       const stdCoupon = process.env.PRIMER_STD_COUPON;
  //       setCouponCode(stdCoupon);
  //       setTimeout(() => {
  //         handleCouponValidation(stdCoupon);
  //       }, 100);
  //     }
  //   }
  // }, [user, page]);

  const calculateTimeLeft = () => {
    const now = new Date();
    const targetDate = new Date("March 16, 2025 18:00:00 EST");
    const difference = targetDate - now;

    let timeLeft = {};

    if (difference > 0) {
      timeLeft = {
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((difference / 1000 / 60) % 60),
        seconds: Math.floor((difference / 1000) % 60),
      };
    }

    return timeLeft;
  };

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(timer);
  }, []);
  return (
    <>
      {isLoading && (
        <div className="loader-container">
          <img src={`/android-chrome-512x512.png`} alt="loading" />
        </div>
      )}
      <div
        className="checkout-form col-lg-5 col-md-7 col-sm-10 mx-auto p-5 bg-white"
        style={{ display: "flex", flexDirection: "column", gap: "15px", borderRadius: "10px" }}
      >
        {page === "marchMadnessPrimer" ? (
          <div className="text-center">
            <h3>Get the full March Madness Primer for $19.99</h3>
            <p style={{ fontSize: "1.2rem", color: "#333" }}>
              {/* Offer expires in{" "}
              <span style={{ color: "#ff4500", fontWeight: "bold" }}>
                {timeLeft.days}d {timeLeft.hours}h {timeLeft.minutes}m {timeLeft.seconds}s
              </span> */}
            </p>
          </div>
        ) : (
          <>
            <h1 className="text-center">Let's go! Activate your {planName[plan]} membership.</h1>
            {!(plan === "day-pass" || plan === "nba-playoff" || plan === "wnba-season") ? (
              <p className="text-center">
                You will be billed <b>${planPrice[plan]}</b> today and your membership will renew {planDuration[plan]}{" "}
                until cancelled
              </p>
            ) : (
              plan === "day-pass" && (
                <p className="text-center">
                  You will be billed <b>$9.99</b> today for 24 hours ALL Access
                </p>
              )
            )}
            {plan === "nba-playoff" && (
              <p className="text-center">
                You will be billed <b>$499.99</b> today for the NBA Playoff Premium membership
              </p>
            )}
            {plan === "wnba-season" && (
              <p className="text-center">
                You will be billed <s style={{ fontWeight: "400" }}> $299.99</s> <b>$199.99</b> today for the WNBA
                Season Premium membership
              </p>
            )}
          </>
        )}
        {errorMessage && (
          <div className="mt-3 text-center" style={{ color: "red" }}>
            {errorMessage}
          </div>
        )}
        {user && (
          <>
            <h3 className="text-center">Welcome {user.name}</h3>
          </>
        )}
        {!user && (
          <>
            {page !== "marchMadnessPrimer" && (
              <Input
                icon={<AiOutlineUser />}
                type="text"
                placeholder="Name"
                onChange={(e) => setName(e.target.value)}
                value={name}
              />
            )}
            <Input
              icon={<AiOutlineMail />}
              type="email"
              className="form-control rounded-pill py-3"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your email"
            />
            {page !== "marchMadnessPrimer" && (
              <>
                <Input
                  icon={<RiLockPasswordLine />}
                  type="password"
                  className="form-control rounded-pill py-3"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="password"
                />
                <Input
                  icon={<RiLockPasswordLine />}
                  type="password"
                  className="form-control rounded-pill py-3"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="confirm password"
                />
              </>
            )}
          </>
        )}
        {!showCouponInput && (
          <button
            onClick={() => {
              setCouponCode("");
              setShowCouponInput(true);
            }}
          >
            Have a discount?
          </button>
        )}

        {showCouponInput && (
          <>
            <Input
              icon={<AiOutlineSearch />}
              type="text"
              placeholder="Enter Coupon Code"
              value={couponCode}
              onChange={(e) => setCouponCode(e.target.value)}
            />
            <div className="d-flex align-items-center justify-content-center mt-2">
              <button
                className="btn btn-dark rounded-pill py-2 mr-2"
                style={{ maxWidth: "200px" }}
                onClick={handleCouponValidation}
              >
                Apply Coupon
              </button>
              <span onClick={() => setShowCouponInput(false)} className="cursor-pointer text-muted">
                Don't have a coupon?
              </span>
            </div>
          </>
        )}

        {couponSuccess && (
          <div className="mt-3 text-center" style={{ color: "green" }}>
            {couponSuccess}
            <p className="text-center">
              🙌🏾 Nice you saved <b>${couponDiscount}</b> off your {planName[plan]} membership
            </p>
          </div>
        )}

        <h4>Payment Info</h4>
        <div className="form-control  rounded-pill py-3" style={{ backgroundColor: "#d0d2d9" }}>
          <CardNumberElement options={CARD_ELEMENT_OPTIONS} />
        </div>
        <div className="row">
          <div className="col m-3 form-control rounded-pill py-3" style={{ backgroundColor: "#d0d2d9" }}>
            <CardExpiryElement options={CARD_ELEMENT_OPTIONS} />
          </div>
          <div className="col m-3 form-control rounded-pill py-3" style={{ backgroundColor: "#d0d2d9" }}>
            <CardCvcElement options={CARD_ELEMENT_OPTIONS} />
          </div>
        </div>
        {/* <CardElement /> */}
        <div className="d-flex align-items-base">
          <Checkbox
            checked={termsChecked}
            onChange={() => setTermsChecked((prev) => !prev)}
            label={
              <span>
                  I have read and understood 
                <a href="/terms-of-service" target="_blank" className="cursor-pointer">
                  <u>ShotQuality's Terms and Conditions</u>
                </a>
              </span>
            }
          />
        </div>
        <div className="d-flex align-items-base">
          <Checkbox
            checked={receivePicksChecked}
            onChange={() => setReceivePicksChecked((prev) => !prev)}
            label={<span>  Receive picks, predictions, and tips</span>}
          />
        </div>
        <div>
          <p className="text-center">
            Your 
            <a href="/privacy-policy" target="_blank" className="cursor-pointer">
              <u>privacy is important to us</u>
            </a>
          </p>
        </div>

        <button className="btn btn-dark btn-block mt-4 rounded-pill py-2" onClick={handleSubmit} disabled={isLoading}>
          {isLoading ? "Processing..." : page === "marchMadnessPrimer" ? "Buy Now" : "SIGN ME UP"}
        </button>
      </div>
    </>
  );
};

export default CheckoutForm;
